/**
 * DscrResultsColumn — the priced DSCR scenario, as the workbench's RIGHT COLUMN.
 *
 * Reads the way a pricing desk reads it: the EXIT PRICE is the answer (this is a
 * loan being sold — the proceeds are the number), the coupon is a term of it,
 * and everything below is the working that produced it.
 *
 *   1. eligibility, and the DSCR band that decided it
 *   2. exit price against the sheet's cap, with the LLPA trail
 *   3. tier + the FICO / LTV buckets that priced it
 *   4. the alternatives — best execution and the best rung per DSCR tier
 *   5. the rate ladder (click a rung to re-price anchored there)
 *   6. rate x leverage outcomes
 *   7. rural determination + market
 *   8. closing costs, rate lock, caveats
 *
 * NOTHING HERE IS A THRESHOLD THE FRONTEND KNOWS. The design this is built to
 * carried a hardcoded `FLOOR = 0.975` and a `priceCap` computed as
 * `1.05 - margin`; both are sheet data that moves per counterparty and per
 * prepay structure, and neither is a field the endpoint returns. They are read
 * instead off the ladder's own `clamped` rows — a rung clamped by a "Max Price"
 * row IS at the cap, and its price IS the cap (see `clampBounds`). Same for the
 * FICO/LTV buckets and the eligibility note: derived from `applied[]`, which is
 * the auditable record of every sheet row the engine touched.
 *
 * RENDER-ONLY (pricing-scenario-sizers.md, "Shipping requirements"): no DSCR, no
 * PITIA, no price, no premium and no matrix cell is computed here. Re-anchoring
 * on a ladder click RE-POSTS the scenario rather than re-deriving locally.
 *
 * PREMIUM IS IN BOND POINTS, and only bond points — trade-proceeds vocabulary,
 * never a borrower-facing cash "credit".
 */
import { useMemo, useState } from "react";

import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import CheckCircleOutlineRounded from "@mui/icons-material/CheckCircleOutlineRounded";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ReportProblemRoundedIcon from "@mui/icons-material/ReportProblemRounded";

import Flx from "../../components/layout/Flx";
import Txt from "../../components/typography/Txt";
import {
  fmtMoney,
  fmtPct,
  fmtRate,
  fmtRatio,
  humanize,
} from "./sizerFormat";
import { SZ, SelectInput, ResultStack } from "./sizerUi";
import { Callout, ClosingCostLadder, GuidelineCheckList } from "./sizerResultUi";
import MarketDataPanel from "../SizerScreen/components/MarketDataPanel";

/* ------------------------------------------------------------------ atoms */

const Card = ({ pad = true, children, sx }) => (
  <Box
    sx={{
      bgcolor: SZ.card,
      border: `1px solid ${SZ.line}`,
      borderRadius: SZ.radius,
      boxShadow: SZ.shadow,
      ...(pad ? { p: "15px 17px" } : null),
      ...sx,
    }}
  >
    {children}
  </Box>
);

const CardTitle = ({ children, right }) => (
  <Flx jb ac g={1} sx={{ mb: 1.4 }}>
    <Txt
      sx={{
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: ".08em",
        textTransform: "uppercase",
        color: SZ.ink400,
      }}
    >
      {children}
    </Txt>
    {right}
  </Flx>
);

const Mono = ({ children, sx }) => (
  <Box
    component="span"
    sx={{ fontFamily: SZ.mono, fontVariantNumeric: "tabular-nums", ...sx }}
  >
    {children}
  </Box>
);

const Row = ({ k, v, src, tone, last }) => (
  <Flx
    jb
    g={1}
    sx={{
      alignItems: "baseline",
      py: "6px",
      borderBottom: last ? "none" : `1px dashed ${SZ.line}`,
    }}
  >
    <Txt sx={{ fontSize: 12.5, color: SZ.ink500 }}>{k}</Txt>
    <Box sx={{ textAlign: "right" }}>
      <Mono
        sx={{
          fontSize: 12.5,
          fontWeight: tone ? 600 : 400,
          color: tone === "pos" ? SZ.good : tone === "neg" ? SZ.bad : SZ.ink900,
        }}
      >
        {v}
      </Mono>
      {src && (
        <Txt component="span" sx={{ fontSize: 10, color: SZ.ink400, ml: 0.75 }}>
          {src}
        </Txt>
      )}
    </Box>
  </Flx>
);

/* ------------------------------------------------------------- formatting */

const MINUS = "−";

/**
 * Price on the PAR-100 scale — 1.0378 renders as "103.780".
 *
 * The desk's quote convention (par = 100). This is NOT the percentage that
 * `fmtPrice`'s "never multiply it by 100" warns against — that warns against
 * reading a bond price AS a rate. Kept local so the RTL rail's 1.0xxxx
 * convention is untouched.
 */
export const fmtExit = (v) =>
  v == null || Number.isNaN(Number(v)) ? "—" : (Number(v) * 100).toFixed(3);

/** Premium / discount in BOND POINTS, straight off `premiumDiscountPts`. */
export const fmtPts = (v, places = 2) =>
  v == null || Number.isNaN(Number(v))
    ? "—"
    : `${Number(v) > 0 ? "+" : Number(v) < 0 ? MINUS : ""}${Math.abs(Number(v)).toFixed(places)}`;

/** An `applied[]` LLPA value is a price delta (-0.005) — 100x is bond points. */
const llpaPts = (v) => (typeof v === "number" ? v * 100 : null);

/* ------------------------------------------------------------- derivations */

/**
 * The sheet's price CLAMPS, read off the ladder.
 *
 * `minMaxPrices` rows live in the counterparty's pricing sheet and move per
 * prepay structure and per tier — the endpoint does not return them as numbers,
 * but it returns `clamped` naming the row that bound each rung, and a bound
 * rung's `price` IS that bound. So the cap and the floor are recovered from the
 * response rather than typed here, which is the whole point: hardcoding 1.05 /
 * 0.975 would silently lie the moment a sheet changes.
 *
 * Returns nulls when no rung hit a clamp — a ladder that never reached the cap
 * genuinely has no cap to show, and an invented one would be worse than none.
 */
export function clampBounds(rows) {
  let cap = null;
  let floor = null;
  let capLabel = null;
  for (const r of rows || []) {
    const label = String(r.clamped || "");
    if (/^\s*max price/i.test(label)) {
      cap = cap == null ? r.price : Math.max(cap, r.price);
      capLabel = label;
    }
    if (/^\s*min price/i.test(label)) {
      floor = floor == null ? r.price : Math.min(floor, r.price);
    }
  }
  return { cap, floor, capLabel };
}

/** `applied` is a list of single-key `{label: value}` dicts — flatten it. */
const flattenApplied = (applied) =>
  (applied || []).flatMap((a) =>
    Object.entries(a || {}).map(([label, value]) => ({ label, value })),
  );

/**
 * The FICO / LTV / DSCR pricing buckets, recovered from `applied[]`.
 *
 * The sheet names the row it used ("FICO: 740 - 759", "1.00 <= DSCR < 1.15"),
 * so the bucket is data, not something to re-derive from the raw score. A
 * bucket the sheet did not apply comes back null and its card is omitted —
 * rendering an empty "LTV bucket" card would imply the sheet priced one.
 */
const bucketOf = (items, test) => {
  const hit = (items || []).find((i) => test(String(i.label).toUpperCase()));
  if (!hit) return null;
  return {
    // "FICO: 740 - 759" -> "740 - 759"; "LTV 70.01 - 75%" -> "70.01 - 75%"
    label: String(hit.label).replace(/^(FICO|LTV)\s*:?\s*/i, ""),
    raw: hit.label,
    pts: llpaPts(hit.value),
  };
};

/* ------------------------------------------------------------------ cards */

/** Eligibility, and the DSCR band that decided how it priced. */
const EligibilityBanner = ({ passing, bandLabel, anchor }) => {
  const tone = passing
    ? { fg: SZ.good, bg: SZ.goodBg, br: "#bfe0d3" }
    : { fg: SZ.bad, bg: SZ.badBg, br: "#efc9c9" };
  const Icon = passing ? CheckCircleOutlineRounded : ReportProblemRoundedIcon;
  return (
    <Flx
      ac
      g={1.25}
      sx={{
        p: "10px 15px",
        borderRadius: SZ.radius,
        background: tone.bg,
        border: `1px solid ${tone.br}`,
      }}
    >
      <Icon sx={{ fontSize: 17, color: tone.fg }} />
      <Txt sx={{ fontSize: 12.5, fontWeight: 600, color: tone.fg }}>
        {passing ? "Eligible" : "Ineligible"}
      </Txt>
      <Txt
        sx={{
          fontSize: 11.5,
          color: tone.fg,
          opacity: 0.85,
          ml: "auto",
          textAlign: "right",
        }}
      >
        {bandLabel
          ? `${bandLabel} band priced with LLPA`
          : anchor === "guideline"
            ? "fails the guidelines — indicative only"
            : anchor === "ladder_mid"
              ? "no rung passed — indicative only"
              : ""}
      </Txt>
    </Flx>
  );
};

/**
 * The guideline box (pricing-scenario-sizers.md §2.x, 2026-10-09): the DSCR
 * quick price screens loan amount, FICO, the DSCR floor and the leverage max
 * BEFORE it prices. A HARD row makes every rung ineligible — rendered as the
 * blocking banner above everything, the same Guideline Check list the RTL
 * sizer uses underneath. A SOFT row (DSCR 0.80–0.99) is an exception-request
 * chip. The price still shows below so the desk sees what it WOULD price at.
 */
const GuidelineCard = ({ exceptions, maxLoan }) => {
  const rows = exceptions || [];
  if (!rows.length) return null;
  const hard = rows.filter((e) => e.severity === "hard");
  const soft = rows.filter((e) => e.severity !== "hard");
  return (
    <Card>
      <CardTitle
        right={
          soft.length ? (
            <Box
              component="span"
              sx={{
                fontSize: 11,
                fontWeight: 600,
                color: SZ.warn,
                background: SZ.warnBg,
                border: `1px solid ${SZ.warn}`,
                borderRadius: "99px",
                px: 1,
                py: "1px",
              }}
            >
              Requires exception request
            </Box>
          ) : null
        }
      >
        Guideline check
      </CardTitle>
      {hard.length > 0 && (
        <Callout tone="bad" title="Doesn't meet the guidelines" sx={{ mb: 1.25 }}>
          {hard.map((e) => e.message).join(" ")}
          {maxLoan != null ? ` Reduce the loan to about ${fmtMoney(maxLoan)} to fit.` : ""}{" "}
          Every rung below is ineligible; the price is shown as an indication only.
        </Callout>
      )}
      <GuidelineCheckList exceptions={rows} />
    </Card>
  );
};

const fmtReduction = (v) =>
  v == null ? "—" : `${Number(v) < 0 ? MINUS : "+"}${fmtPct(Math.abs(Number(v)), 0)}`;

/**
 * The Guidelines v8.0 leverage matrix (2026-10-09): the grid max for the FICO
 * bucket × purpose, the reductions that stacked onto it, and requested vs the
 * effective max — "Max LTV 75% (grid 80% − 5% 5-9 units)". Over the max is a
 * hard ltv_above_max / ltc_above_max row in the Guideline check above.
 */
const LeverageCard = ({ leverage, maxLoan }) => {
  if (!leverage || (leverage.maxLtv == null && leverage.gridMaxLtv == null)) return null;
  const reductions = leverage.reductions || [];
  const detail = (gridMax) =>
    gridMax == null
      ? null
      : `grid ${fmtPct(gridMax, 0)}${reductions
          .map((r) => ` ${fmtReduction(r.reduction)} ${r.reason}`)
          .join("")}`;
  return (
    <Card>
      <CardTitle>Leverage</CardTitle>
      <Row
        k="Max LTV"
        v={fmtPct(leverage.maxLtv, 0)}
        src={reductions.length ? detail(leverage.gridMaxLtv) : "guideline grid"}
      />
      <Row
        k="Requested LTV"
        v={fmtPct(leverage.requestedLtv, 2)}
        tone={leverage.overLtv ? "neg" : undefined}
        last={leverage.maxLtc == null}
      />
      {leverage.maxLtc != null && (
        <>
          <Row
            k="Max LTC"
            v={fmtPct(leverage.maxLtc, 0)}
            src={reductions.length ? detail(leverage.gridMaxLtc) : "guideline grid"}
          />
          <Row
            k="Requested LTC"
            v={fmtPct(leverage.requestedLtc, 2)}
            tone={leverage.overLtc ? "neg" : undefined}
            last
          />
        </>
      )}
      {reductions.length > 0 && (
        <Flx g={0.75} sx={{ mt: 1.25, flexWrap: "wrap" }}>
          {reductions.map((r) => (
            <Tooltip key={r.code} title={r.citation || ""} arrow>
              <Box
                component="span"
                sx={{
                  fontSize: 11.5,
                  fontWeight: 600,
                  color: SZ.warn,
                  background: SZ.warnBg,
                  borderRadius: "99px",
                  px: 1.1,
                  py: "2px",
                }}
              >
                {fmtReduction(r.reduction)} {r.reason}
              </Box>
            </Tooltip>
          ))}
        </Flx>
      )}
      {(leverage.overLtv || leverage.overLtc) && maxLoan != null && (
        <Txt component="div" sx={{ mt: 1.25, fontSize: 12.5, color: SZ.bad, fontWeight: 600 }}>
          Reduce the loan to ~{fmtMoney(maxLoan)} to fit the max.
        </Txt>
      )}
      <Txt component="div" sx={{ mt: 1, fontSize: 11, color: SZ.ink400 }}>
        {[leverage.ficoBucket && `FICO ${leverage.ficoBucket}`, leverage.loanPurpose, leverage.citation]
          .filter(Boolean)
          .join(" · ")}
      </Txt>
    </Card>
  );
};

/**
 * The exit price, against the sheet's cap. Dark because it is the answer.
 *
 * The bar spans the sheet's own floor -> cap when the ladder reached both; when
 * it reached neither there is nothing real to plot, so the bar is omitted
 * rather than scaled against an invented range.
 */
const ExitHero = ({ res, bounds, llpas }) => {
  const price = res?.price;
  const headroom =
    bounds.cap != null && price != null ? (bounds.cap - price) * 100 : null;

  const lo = bounds.floor;
  const hi = bounds.cap;
  const plottable = lo != null && hi != null && hi > lo && price != null;
  const fillPct = plottable
    ? Math.max(2, Math.min(100, ((price - lo) / (hi - lo)) * 100))
    : 0;

  const stats = [
    { k: "DSCR", v: fmtRatio(res?.dscr, 4), warn: Number(res?.dscr) < 1 },
    { k: "LTV", v: fmtPct(res?.ltv, 2) },
    { k: "PITIA /mo", v: fmtMoney(res?.pitia) },
    { k: "Rent /mo", v: fmtMoney(res?.qualifyingRent) },
  ];

  return (
    <Card pad={false} sx={{ border: 0, overflow: "hidden" }}>
      <Box sx={{ background: SZ.heroBg, color: "#fff", p: "17px 17px 15px" }}>
        <Txt
          component="div"
          sx={{
            fontSize: 10.5,
            fontWeight: 600,
            letterSpacing: ".09em",
            textTransform: "uppercase",
            color: SZ.heroMuted,
          }}
        >
          Exit price
        </Txt>

        <Flx
          g={2.5}
          sx={{ alignItems: "flex-end", mt: "5px", flexWrap: "wrap" }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Txt
              component="div"
              sx={{
                fontFamily: SZ.mono,
                fontSize: 33,
                fontWeight: 600,
                letterSpacing: "-.02em",
                lineHeight: 1,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {fmtExit(price)}
            </Txt>
            {bounds.cap != null && (
              <Txt
                component="div"
                sx={{ fontSize: 11.5, color: SZ.heroMuted, mt: "5px" }}
              >
                cap{" "}
                <Mono sx={{ color: "#fff", fontWeight: 500 }}>
                  {fmtExit(bounds.cap)}
                </Mono>
                {headroom != null && (
                  <>
                    {" · headroom "}
                    <Mono sx={{ color: "#fff", fontWeight: 500 }}>
                      {headroom.toFixed(2)} pts
                    </Mono>
                  </>
                )}
              </Txt>
            )}
          </Box>

          <Flx g={2.25} sx={{ ml: "auto" }}>
            {[
              { k: "Coupon", v: fmtRate(res?.coupon) },
              { k: "Net pts", v: fmtPts(res?.premiumDiscountPts, 3) },
            ].map((s) => (
              <Box key={s.k}>
                <Txt
                  component="div"
                  sx={{
                    fontFamily: SZ.mono,
                    fontSize: 16,
                    fontWeight: 600,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {s.v}
                </Txt>
                <Txt
                  component="div"
                  sx={{ fontSize: 10.5, color: SZ.heroMuted, mt: "2px" }}
                >
                  {s.k}
                </Txt>
              </Box>
            ))}
          </Flx>
        </Flx>

        {plottable && (
          <Box sx={{ mt: "13px" }}>
            <Box
              sx={{
                height: 7,
                borderRadius: "99px",
                background: SZ.heroChip,
                position: "relative",
              }}
            >
              <Box
                sx={{
                  height: "100%",
                  width: `${fillPct}%`,
                  borderRadius: "99px",
                  background: `linear-gradient(90deg, ${SZ.teal700}, ${SZ.teal500})`,
                  transition: "width .35s cubic-bezier(.2,.8,.2,1)",
                }}
              />
              {/* The cap marker. Amber, because reaching it is the sheet
                  refusing to pay more — a boundary, not an achievement. */}
              <Box
                sx={{
                  position: "absolute",
                  top: -3,
                  bottom: -3,
                  right: 0,
                  width: 2,
                  borderRadius: "2px",
                  background: SZ.heroWarn,
                }}
              />
            </Box>
            <Flx
              jb
              sx={{
                mt: "5px",
                fontFamily: SZ.mono,
                fontSize: 10,
                color: SZ.heroMuted,
              }}
            >
              <span>{fmtExit(lo)}</span>
              <span>cap {fmtExit(hi)}</span>
            </Flx>
          </Box>
        )}

        <Flx
          sx={{
            borderTop: `1px solid #2a3a57`,
            mt: "13px",
            pt: "11px",
          }}
        >
          {stats.map((s) => (
            <Box key={s.k} sx={{ flex: 1, minWidth: 0 }}>
              <Txt
                component="div"
                sx={{
                  fontFamily: SZ.mono,
                  fontSize: 14,
                  fontWeight: 600,
                  fontVariantNumeric: "tabular-nums",
                  color: s.warn ? SZ.heroWarn : "#fff",
                }}
              >
                {s.v}
              </Txt>
              <Txt
                component="div"
                sx={{ fontSize: 10, color: SZ.heroMuted, mt: "2px" }}
              >
                {s.k}
              </Txt>
            </Box>
          ))}
        </Flx>
      </Box>

      {llpas.length > 0 && (
        <Flx
          g={0.75}
          sx={{ background: SZ.heroPanel, p: "11px 15px 13px", flexWrap: "wrap" }}
        >
          {llpas.map((l, i) => {
            const pts = llpaPts(l.value);
            return (
              <Txt
                key={`${l.label}-${i}`}
                sx={{
                  fontFamily: SZ.mono,
                  fontSize: 10.5,
                  color: SZ.heroChipInk,
                  background: SZ.heroChip,
                  borderRadius: "5px",
                  p: "3px 7px",
                  whiteSpace: "nowrap",
                }}
              >
                {l.label}{" "}
                {pts != null ? (
                  <Box
                    component="span"
                    sx={{ color: pts >= 0 ? "#6ee7c8" : "#f3a1a1" }}
                  >
                    {fmtPts(pts)}
                  </Box>
                ) : (
                  <Box component="span" sx={{ color: SZ.heroWarn }}>
                    {String(l.value)}
                  </Box>
                )}
              </Txt>
            );
          })}
        </Flx>
      )}
    </Card>
  );
};

/** Tier, the margin it applied, and the buckets the sheet actually priced. */
const TierBucketsCard = ({ res, fico, ltv }) => {
  const tier = res?.tier || "BASE";
  const margin = res?.marginPointsApplied;
  const tone =
    tier === "GOLD"
      ? { fg: SZ.warn, bg: SZ.warnBg, br: "#eed9b8" }
      : tier === "SILVER"
        ? { fg: "#5e6b85", bg: "#eff1f6", br: "#d4dae6" }
        : { fg: SZ.ink500, bg: SZ.bg, br: SZ.lineStrong };

  const buckets = [
    fico ? { k: "FICO bucket", ...fico } : null,
    ltv ? { k: "LTV bucket", ...ltv } : null,
  ].filter(Boolean);

  return (
    <Card>
      <CardTitle>Tier &amp; pricing buckets</CardTitle>
      <Flx ac g={1.25} sx={{ mb: 1.5, flexWrap: "wrap" }}>
        <Txt
          sx={{
            fontFamily: SZ.mono,
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: ".04em",
            color: tone.fg,
            background: tone.bg,
            border: `1px solid ${tone.br}`,
            borderRadius: "7px",
            p: "5px 12px",
          }}
        >
          {tier}
        </Txt>
        <Txt sx={{ fontSize: 12.5, color: SZ.ink500 }}>
          {margin == null ? (
            "No correspondent margin applied"
          ) : (
            <>
              Margin{" "}
              <Mono sx={{ color: SZ.ink900, fontWeight: 600 }}>
                {MINUS}
                {Number(margin).toFixed(3)} pts
              </Mono>{" "}
              <Box component="span" sx={{ color: SZ.ink400 }}>
                applied to exit
              </Box>
            </>
          )}
        </Txt>
      </Flx>

      {buckets.length > 0 && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: buckets.length > 1 ? "1fr 1fr" : "1fr",
            gap: 1.25,
          }}
        >
          {buckets.map((b) => (
            <Box
              key={b.k}
              sx={{
                border: `1px solid ${SZ.teal100}`,
                background: SZ.teal50,
                borderRadius: SZ.radiusSm,
                p: "10px 12px",
              }}
            >
              <Txt
                component="div"
                sx={{
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: ".07em",
                  textTransform: "uppercase",
                  color: SZ.ink400,
                }}
              >
                {b.k}
              </Txt>
              <Txt
                component="div"
                sx={{
                  fontFamily: SZ.mono,
                  fontSize: 14,
                  fontWeight: 600,
                  color: SZ.teal700,
                  fontVariantNumeric: "tabular-nums",
                  mt: "3px",
                }}
              >
                {b.label}
              </Txt>
              <Txt
                component="div"
                sx={{
                  fontSize: 10.5,
                  color: SZ.ink400,
                  fontFamily: SZ.mono,
                  mt: "2px",
                }}
              >
                {b.pts == null ? "—" : `${fmtPts(b.pts)} pts LLPA`}
              </Txt>
            </Box>
          ))}
        </Box>
      )}
    </Card>
  );
};

/**
 * The alternatives to the anchored answer — `bandBests` is the headline
 * comparison ("best outcome in the thin tier vs the healthy tier"), and
 * `breakeven` is last, small and unbadged: the contract is explicit that the
 * thinnest passing rung is an informational boundary, never a recommendation.
 */
const AlternativesCard = ({ bandBests, bestExecution, breakeven, anchorCoupon }) => {
  const bands = Object.entries(bandBests || {});
  if (!bands.length && !bestExecution && !breakeven) return null;

  const line = (r) =>
    r ? `${fmtRate(r.coupon)} · ${fmtExit(r.price)}` : "no passing rung";

  return (
    <Card>
      <CardTitle>Alternatives</CardTitle>
      {bestExecution && (
        <Row
          k="Best execution"
          v={line(bestExecution)}
          src={
            bestExecution.coupon === anchorCoupon
              ? "this rung"
              : `${fmtPts(bestExecution.premiumDiscountPts)} pts`
          }
        />
      )}
      {bands.map(([key, rung], i) => (
        <Row
          key={key}
          k={key.endsWith("+") ? `Best at DSCR ${key}` : `Best at DSCR ${key}x`}
          v={line(rung)}
          src={rung ? fmtRatio(rung.dscr, 2) : undefined}
          last={i === bands.length - 1 && !breakeven}
        />
      ))}
      {breakeven && (
        <Txt sx={{ fontSize: 11, color: SZ.ink400, mt: 1, display: "block" }}>
          Qualifies up to{" "}
          <Mono sx={{ color: SZ.ink500 }}>{fmtRate(breakeven.coupon)}</Mono> at{" "}
          <Mono sx={{ color: SZ.ink500 }}>{fmtRatio(breakeven.dscr, 2)}</Mono> —
          the thinnest passing rung, shown as a boundary, not a recommendation.
        </Txt>
      )}
    </Card>
  );
};

/**
 * The rate ladder. Clicking a rung RE-PRICES anchored there — it does not
 * re-derive the answer locally, which is the single-calc rule and the reason
 * the reference implementation's client-side re-anchor could not be kept.
 *
 * A fixed readout of ONE priced scenario inside a 420px rail, with nothing to
 * sort, filter, page or persist: the CLAUDE.md carve-out from the AG Grid rule,
 * same as the RTL rail's leverage table.
 */
const LadderCard = ({
  rows,
  anchorCoupon,
  capLabel,
  onReanchor,
  busy,
  pendingCoupon,
}) => {
  const [showAll, setShowAll] = useState(false);
  const priceable = useMemo(
    () => (rows || []).filter((r) => !r.ineligible),
    [rows],
  );
  const shown = showAll || !priceable.length ? rows : priceable;
  if (!rows?.length) return null;
  const hidden = priceable.length ? rows.length - priceable.length : 0;

  const th = {
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: ".06em",
    textTransform: "uppercase",
    color: SZ.ink400,
    textAlign: "right",
    padding: "0 0 7px",
    borderBottom: `1px solid ${SZ.line}`,
    whiteSpace: "nowrap",
    position: "sticky",
    top: 0,
    background: SZ.card,
    zIndex: 1,
  };
  const td = {
    padding: "6px 0",
    borderBottom: `1px dashed ${SZ.line}`,
    fontFamily: SZ.mono,
    fontVariantNumeric: "tabular-nums",
    textAlign: "right",
    fontSize: 12,
    whiteSpace: "nowrap",
  };

  return (
    <Card>
      <CardTitle
        right={
          <Flx ac g={1.5}>
            {hidden > 0 && (
              <Box
                component="button"
                type="button"
                onClick={() => setShowAll((v) => !v)}
                sx={{
                  border: 0,
                  background: "none",
                  p: 0,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  fontSize: 11,
                  fontWeight: 600,
                  color: SZ.teal700,
                }}
              >
                {showAll ? "Hide" : `Show ${hidden} ineligible`}
              </Box>
            )}
            {onReanchor &&
              (busy ? (
                /* The rail keeps showing the PREVIOUS answer during a re-price
                   — that is deliberate, an empty column mid-iterate is worse —
                   so the ladder has to say for itself that a new one is coming.
                   Without this the only feedback is the action bar, which is
                   pinned to the window and easy to miss from here. */
                <Flx ac g={0.75}>
                  <CircularProgress size={11} sx={{ color: SZ.teal700 }} />
                  <Txt
                    sx={{
                      fontSize: 10,
                      color: SZ.teal700,
                      fontWeight: 600,
                      textTransform: "none",
                      letterSpacing: 0,
                    }}
                  >
                    {pendingCoupon != null
                      ? `Re-pricing at ${fmtRate(pendingCoupon)}…`
                      : "Re-pricing…"}
                  </Txt>
                </Flx>
              ) : (
                <Txt
                  sx={{
                    fontSize: 10,
                    color: SZ.ink400,
                    textTransform: "none",
                    letterSpacing: 0,
                    fontWeight: 500,
                  }}
                >
                  click to re-price
                </Txt>
              ))}
          </Flx>
        }
      >
        Rate ladder
      </CardTitle>

      <Box
        sx={{
          maxHeight: 218,
          overflowY: "auto",
          mx: "-17px",
          px: "17px",
          "&::-webkit-scrollbar": { width: 5 },
          "&::-webkit-scrollbar-thumb": {
            background: SZ.lineStrong,
            borderRadius: "99px",
          },
        }}
      >
        <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
          <Box component="thead">
            <Box component="tr">
              <Box component="th" sx={{ ...th, textAlign: "left" }}>
                Coupon
              </Box>
              <Box component="th" sx={th}>
                DSCR
              </Box>
              <Box component="th" sx={th}>
                Exit
              </Box>
              <Box component="th" sx={th}>
                Pts
              </Box>
            </Box>
          </Box>
          <Box component="tbody">
            {shown.map((r) => {
              const isAnchor = r.coupon === anchorCoupon;
              const isPending = busy && r.coupon === pendingCoupon;
              const dim = !r.passing;
              return (
                <Box
                  component="tr"
                  key={r.coupon}
                  onClick={
                    onReanchor && !busy ? () => onReanchor(r.coupon) : undefined
                  }
                  sx={{
                    cursor: onReanchor && !busy ? "pointer" : "default",
                    // The row being priced holds the eye while the request is in
                    // flight; the rest fade so the table reads as stale, which
                    // it is until the response lands.
                    ...(isPending
                      ? { background: SZ.teal100 }
                      : isAnchor
                        ? { background: SZ.teal50 }
                        : { "&:hover td": { background: SZ.bg } }),
                    ...(busy && !isPending ? { opacity: 0.45 } : null),
                  }}
                >
                  <Box
                    component="td"
                    sx={{
                      ...td,
                      textAlign: "left",
                      fontWeight: isAnchor ? 600 : 400,
                      color: isAnchor ? SZ.teal700 : dim ? SZ.ink400 : SZ.ink900,
                    }}
                  >
                    {isPending && (
                      <CircularProgress
                        size={9}
                        sx={{ color: SZ.teal700, mr: "6px", verticalAlign: "0" }}
                      />
                    )}
                    {fmtRate(r.coupon)}
                  </Box>
                  <Box
                    component="td"
                    sx={{
                      ...td,
                      fontWeight: isAnchor ? 600 : 400,
                      color: isAnchor ? SZ.teal700 : dim ? SZ.ink400 : SZ.ink900,
                    }}
                  >
                    {fmtRatio(r.dscr, 4)}
                  </Box>
                  <Box
                    component="td"
                    sx={{
                      ...td,
                      fontWeight: isAnchor ? 600 : 400,
                      color: isAnchor ? SZ.teal700 : dim ? SZ.ink400 : SZ.ink900,
                    }}
                  >
                    {fmtExit(r.price)}
                    {r.clamped && (
                      <Tooltip title={r.clamped} arrow>
                        <Box
                          component="span"
                          sx={{
                            display: "inline-block",
                            width: 6,
                            height: 6,
                            borderRadius: "99px",
                            ml: "5px",
                            verticalAlign: "1px",
                            background: SZ.warn,
                          }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                  <Box
                    component="td"
                    sx={{
                      ...td,
                      fontWeight: isAnchor ? 600 : 400,
                      color: isAnchor
                        ? SZ.teal700
                        : dim
                          ? SZ.ink400
                          : Number(r.premiumDiscountPts) >= 0
                            ? SZ.good
                            : SZ.bad,
                    }}
                  >
                    {fmtPts(r.premiumDiscountPts)}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      <Txt sx={{ fontSize: 10.5, color: SZ.ink400, mt: 1, display: "block" }}>
        Exit is the sale price on the par-100 scale; Pts is premium against par.
        {capLabel ? ` A dot marks a rung the sheet clamped (${capLabel}).` : ""}
      </Txt>
    </Card>
  );
};

/**
 * Rate × leverage — every cell a FULL server-side reprice (LLPA bucket, UPB
 * band, DSCR band and clamp all re-evaluate). Rows at the guideline max LTV are
 * FLAGGED, not dropped: "you asked for more leverage than the guideline allows,
 * here is what the cap prices at" is the answer.
 */
const OutcomeMatrixCard = ({ matrix }) => {
  const columns = matrix?.columns || [];
  const rows = matrix?.rows || [];
  if (!columns.length || !rows.length) return null;

  return (
    <Card>
      <CardTitle>Rate &times; leverage</CardTitle>
      <Box sx={{ overflowX: "auto", mx: "-4px", px: "4px" }}>
        <Box
          component="table"
          sx={{ width: "100%", minWidth: 320, borderCollapse: "collapse" }}
        >
          <Box component="thead">
            <Box component="tr">
              <Box
                component="th"
                sx={{
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: ".05em",
                  textTransform: "uppercase",
                  color: SZ.ink400,
                  textAlign: "left",
                  padding: "0 6px 7px 0",
                  borderBottom: `1px solid ${SZ.line}`,
                  whiteSpace: "nowrap",
                }}
              >
                Loan
              </Box>
              {columns.map((c) => (
                <Box
                  component="th"
                  key={c.coupon}
                  sx={{
                    fontFamily: SZ.mono,
                    fontSize: 10.5,
                    fontWeight: c.isAnchor ? 700 : 500,
                    color: c.isAnchor ? SZ.teal700 : SZ.ink500,
                    textAlign: "center",
                    padding: "0 4px 7px",
                    borderBottom: `1px solid ${SZ.line}`,
                    whiteSpace: "nowrap",
                  }}
                >
                  {(Number(c.coupon) * 100).toFixed(3)}
                </Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            {rows.map((r) => (
              <Box component="tr" key={`${r.loanAmount}-${r.deltaPct}`}>
                <Box
                  component="td"
                  sx={{
                    padding: "6px 6px 6px 0",
                    borderBottom: `1px dashed ${SZ.line}`,
                    fontFamily: SZ.mono,
                    fontVariantNumeric: "tabular-nums",
                    fontSize: 11,
                    whiteSpace: "nowrap",
                    fontWeight: r.isBase ? 700 : 400,
                    color: SZ.ink700,
                    background: r.isBase ? SZ.teal50 : "transparent",
                  }}
                >
                  {fmtMoney(r.loanAmount)}
                  <Txt
                    component="div"
                    sx={{
                      fontSize: 9.5,
                      color: r.cappedAtGuidelineMax ? SZ.warn : SZ.ink400,
                      fontFamily: "inherit",
                    }}
                  >
                    {r.cappedAtGuidelineMax
                      ? "capped at max"
                      : r.ltv != null
                        ? fmtPct(r.ltv, 1)
                        : ""}
                  </Txt>
                </Box>
                {r.cells.map((cell, ci) => {
                  const anchorCol = columns[ci]?.isAnchor;
                  return (
                    <Box
                      component="td"
                      key={ci}
                      sx={{
                        padding: "6px 4px",
                        textAlign: "center",
                        fontFamily: SZ.mono,
                        fontVariantNumeric: "tabular-nums",
                        fontSize: 11,
                        lineHeight: 1.25,
                        borderBottom: `1px dashed ${SZ.line}`,
                        background:
                          anchorCol && r.isBase
                            ? SZ.teal100
                            : anchorCol || r.isBase
                              ? SZ.teal50
                              : "transparent",
                        color: cell?.passing ? SZ.ink900 : SZ.ink400,
                      }}
                    >
                      <Box sx={{ fontWeight: 600 }}>{fmtRatio(cell?.dscr, 2)}</Box>
                      <Box
                        sx={{
                          fontSize: 10,
                          color: !cell?.passing
                            ? SZ.ink400
                            : Number(cell?.premiumDiscountPts) >= 0
                              ? SZ.good
                              : SZ.bad,
                        }}
                      >
                        {fmtPts(cell?.premiumDiscountPts)}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
      <Txt sx={{ fontSize: 10.5, color: SZ.ink400, mt: 1, display: "block" }}>
        DSCR above, premium in bond points below. {matrix?.note}
      </Txt>
    </Card>
  );
};

/**
 * The CFPB rural determination — always with its METHOD and SOURCE, because the
 * ladder runs FIPS-first (full address -> HouseCanary census / census.gov) down
 * to an attributed county-name match, and the confidence differs a lot between
 * them. The attribution is what says how much the answer is worth.
 */
const RuralCard = ({ ruralCheck, exception }) => {
  if (!ruralCheck) return null;
  return (
    <Card>
      <CardTitle
        right={
          <Txt
            sx={{
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: ".04em",
              textTransform: "uppercase",
              color: ruralCheck.isRural ? SZ.warn : SZ.good,
              background: ruralCheck.isRural ? SZ.warnBg : SZ.goodBg,
              borderRadius: "5px",
              p: "2px 8px",
              whiteSpace: "nowrap",
            }}
          >
            {ruralCheck.isRural ? "Rural" : "Not rural"}
          </Txt>
        }
      >
        CFPB rural check
      </CardTitle>
      <Row
        k="County"
        v={[ruralCheck.county, ruralCheck.state].filter(Boolean).join(", ") || "—"}
        src={ruralCheck.fips ? `FIPS ${ruralCheck.fips}` : undefined}
      />
      <Row k="Method" v={humanize(ruralCheck.method || "—")} last={!exception} />
      {exception?.requested && <Row k="Exception" v="Requested" tone="neg" last />}
      {exception?.note && (
        <Txt sx={{ fontSize: 11, color: SZ.ink500, mt: 0.75, display: "block" }}>
          {exception.note}
        </Txt>
      )}
      {ruralCheck.source && (
        <Txt sx={{ fontSize: 10.5, color: SZ.ink400, mt: 1, display: "block" }}>
          {ruralCheck.source}
        </Txt>
      )}
    </Card>
  );
};

/**
 * Rate lock — the reference design's card, CLIENT-SIDE ONLY.
 *
 * Read this before wiring anything to it: pressing the button records NOTHING
 * server-side. There is no endpoint behind it, by design of the backend rather
 * than by omission here — every lock verb in eastview-os is
 * `/pricing/*_lock/{dealId}` against a real deal, writes to
 * `loan_processing.loan_locks`, mirrors onto `deal.loanLock`, and confirmation
 * is capital-markets gated (403 otherwise). A sizer scenario has no dealId. The
 * sizer's actual role in that workflow runs the OTHER direction: it is attached
 * to a lock request as `loan_sizer`, a loan document (rate-locks.md).
 *
 * The reference implementation is the same — its handler sets a local `locked`
 * flag and rewrites the note; it calls no API either. So this holds the price
 * the desk intends to lock and says plainly that it is not a desk lock yet.
 * When the scenario can be saved as a deal, `onLock` becomes the real
 * `request_dscr_lock` call and the copy below is what changes.
 *
 * Windows are a FE list, not a sheet vocabulary — `lock_period_days` is a free
 * integer on the lock model, so these are offered options, not enum values.
 */
const LOCK_WINDOWS = [30, 45, 60];

/**
 * TEMPORARILY HIDDEN on both DSCR sizers.
 *
 * The card is finished and wired; what it is waiting on is a real lock path from
 * a scenario. Until a scenario can be saved as a deal, pressing the button
 * records nothing anywhere — and a "Locked ✓" that persists nothing is worse
 * than no affordance at all on a screen a desk quotes from.
 *
 * Flip to `true` to bring it back; the component below is unchanged and needs no
 * other edit.
 */
const SHOW_RATE_LOCK = false;

const RateLockCard = ({ res, canLock }) => {
  const [days, setDays] = useState(45);
  const [locked, setLocked] = useState(null);

  const detail = `${fmtExit(res?.price)} @ ${fmtRate(res?.coupon)}`;

  const lock = () => {
    const expires = new Date();
    expires.setDate(expires.getDate() + Number(days));
    setLocked({ days, expires });
  };

  return (
    <Card pad={false}>
      <Flx ac g={1} sx={{ p: "13px 17px 0" }}>
        <LockOutlinedIcon sx={{ fontSize: 15, color: SZ.teal700 }} />
        <Txt sx={{ fontSize: 13, fontWeight: 600, color: SZ.ink900 }}>
          Rate lock
        </Txt>
      </Flx>

      <Flx g={1.25} sx={{ p: "12px 17px 15px", alignItems: "flex-end" }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Txt
            component="div"
            sx={{
              fontSize: 11.5,
              fontWeight: 500,
              color: SZ.ink500,
              mb: "5px",
            }}
          >
            Lock window
          </Txt>
          <SelectInput
            value={days}
            onChange={(v) => setDays(v)}
            disabled={Boolean(locked)}
            options={LOCK_WINDOWS.map((d) => ({
              value: d,
              label: `${d} days`,
            }))}
          />
        </Box>
        <Box
          component="button"
          type="button"
          disabled={!canLock || Boolean(locked)}
          onClick={lock}
          sx={{
            fontFamily: "inherit",
            fontSize: 13,
            fontWeight: 600,
            lineHeight: 1.3,
            color: "#fff",
            background: locked ? SZ.good : SZ.teal600,
            border: 0,
            borderRadius: SZ.radiusSm,
            p: "10px 16px",
            cursor: !canLock || locked ? "not-allowed" : "pointer",
            whiteSpace: "nowrap",
            transition: "background .15s",
            "&:hover": !canLock || locked ? {} : { background: SZ.teal700 },
            "&:disabled": !locked
              ? { background: SZ.lineStrong, color: "#fff" }
              : {},
          }}
        >
          {locked ? "Locked ✓" : "Lock at this price"}
          <Box
            component="span"
            sx={{
              display: "block",
              fontWeight: 400,
              fontSize: 10.5,
              opacity: 0.85,
              fontFamily: SZ.mono,
            }}
          >
            {detail}
          </Box>
        </Box>
      </Flx>

      <Txt
        component="div"
        sx={{ p: "0 17px 13px", fontSize: 11, color: SZ.ink400, lineHeight: 1.5 }}
      >
        {locked ? (
          <>
            Held at {detail} for {locked.days} days · through{" "}
            {locked.expires.toLocaleDateString([], {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
            .{" "}
            <Box component="span" sx={{ color: SZ.warn, fontWeight: 600 }}>
              Not a desk lock —
            </Box>{" "}
            nothing is recorded until this scenario is saved as a deal and the
            lock is requested from the drilldown.
          </>
        ) : canLock ? (
          "Holds the exit price and coupon shown above through the window. A desk lock is requested on the loan once the scenario is saved as a deal."
        ) : (
          "No rung passes at these inputs — there is no price to hold."
        )}
      </Txt>
    </Card>
  );
};

/** Indicative-terms caveats, built from what the sheet actually did. */
const CaveatsCard = ({ res, bandLabel, anchorRung, capLabel }) => {
  const items = [
    res?.dscr != null && bandLabel
      ? `DSCR ${fmtRatio(res.dscr, 4)} — ${bandLabel} band, LLPA applied`
      : null,
    res?.marginPointsApplied != null
      ? `Exit is net of ${res.tier || "correspondent"} margin ${Number(
          res.marginPointsApplied,
        ).toFixed(3)} pts`
      : null,
    anchorRung?.clamped
      ? `Price clamped by the sheet — ${anchorRung.clamped}`
      : capLabel
        ? `Rungs marked with a dot are clamped at the sheet's cap`
        : null,
    "Quick price only — full pricing runs guideline gates and market checks at save",
  ].filter(Boolean);

  return (
    <Card>
      <Callout tone="warn" title="Indicative — terms subject to change">
        <Box component="ul" sx={{ m: 0, pl: 2 }}>
          {items.map((c, i) => (
            <Box
              component="li"
              key={i}
              sx={{ fontSize: 11.5, lineHeight: 1.55, mb: "3px" }}
            >
              {c}
            </Box>
          ))}
        </Box>
      </Callout>
    </Card>
  );
};

/**
 * The eligibility verdict the banner shows, as data — read off the response's
 * own flags (`eligible`, the ladder rows' `passing`), nothing recomputed.
 * Shared with the scenario workspace's Review KPI strip so the two never
 * disagree.
 */
export function dscrEligibility(result) {
  const rows = result?.ladder || [];
  const res = result?.result || null;
  const anchorRung = rows.find((r) => r.coupon === res?.coupon) || null;
  const guidelineEligible = result?.eligible !== false && res?.eligible !== false;
  return {
    passing:
      guidelineEligible && rows.some((r) => r.passing) && Boolean(anchorRung?.passing),
    guidelineEligible,
    anchor: guidelineEligible ? res?.anchor : "guideline",
  };
}

/* ------------------------------------------------------------------ column */

export default function DscrResultsColumn({
  result,
  pending,
  error,
  ruralGate,
  zip,
  onReanchor,
  pendingCoupon,
  /* Flow the cards into CSS multi-columns instead of one rail-width stack —
     for wide surfaces like the Priced Scenarios result panel. At rail width it
     resolves to a single column, so the sizers are unaffected. */
  masonry,
  /* Drop the exit-price card — the scenario workspace's Review mode shows the
     exit price, coupon and net points in its KPI strip instead. */
  hideHero,
}) {
  const rows = useMemo(() => result?.ladder || [], [result]);
  const res = result?.result || null;
  const inputs = result?.inputs || {};

  const bounds = useMemo(() => clampBounds(rows), [rows]);
  const anchorRung = useMemo(
    () => rows.find((r) => r.coupon === res?.coupon) || null,
    [rows, res],
  );
  const llpas = useMemo(() => flattenApplied(anchorRung?.applied), [anchorRung]);
  const fico = useMemo(() => bucketOf(llpas, (l) => l.startsWith("FICO")), [llpas]);
  const ltv = useMemo(() => bucketOf(llpas, (l) => l.startsWith("LTV")), [llpas]);
  // The DSCR band row the sheet applied ("1.00 <= DSCR < 1.15", "DSCR >=1.15")
  // — the response's own words for why it priced the way it did.
  const bandLabel = useMemo(
    () => llpas.find((l) => String(l.label).toUpperCase().includes("DSCR"))?.label,
    [llpas],
  );

  // `result.coupon` is NOT proof of a win: with no passing rung the endpoint
  // falls back to the ladder's MIDDLE coupon so the block still has one. The
  // rows carry the truth.
  const hasPassing = rows.some((r) => r.passing);

  if (error && !result) {
    return (
      <Card>
        <Flx g={1} sx={{ alignItems: "flex-start" }}>
          <ErrorOutlineRoundedIcon sx={{ color: SZ.bad, fontSize: 18 }} />
          <Txt sx={{ fontSize: 13, color: SZ.bad }}>
            {error.message || "Failed to price this scenario."}
          </Txt>
        </Flx>
      </Card>
    );
  }

  // The rural gate is a 422 that CARRIES an answer — the determination and the
  // market block — so it renders as the column, not as an error beside it. The
  // action that clears it lives in the form, where the re-POST is.
  if (ruralGate && !result) {
    return (
      <Flx column g={1.75}>
        <Card>
          <Callout tone="warn" title="Rural property — exception required">
            {ruralGate.reason ||
              "CFPB rural county — DSCR pricing requires an exception request to proceed."}
          </Callout>
        </Card>
        <RuralCard ruralCheck={ruralGate.ruralCheck} />
        <MarketDataPanel zipcode={zip} marketData={ruralGate.marketData} />
      </Flx>
    );
  }

  if (pending && !result) {
    return (
      <Card>
        <Flx center g={1.5} sx={{ py: 4 }}>
          <CircularProgress size={20} sx={{ color: SZ.teal700 }} />
          <Txt sx={{ fontSize: 13, color: SZ.ink500 }}>Pricing…</Txt>
        </Flx>
      </Card>
    );
  }

  if (!result) {
    return (
      <Card sx={{ p: "30px 20px", textAlign: "center" }}>
        <Flx
          center
          sx={{
            width: 40,
            height: 40,
            borderRadius: SZ.radius,
            background: SZ.teal50,
            color: SZ.teal700,
            m: "0 auto 12px",
          }}
        >
          <DescriptionOutlinedIcon sx={{ fontSize: 20 }} />
        </Flx>
        <Txt
          component="div"
          sx={{ fontSize: 13.5, fontWeight: 600, color: SZ.ink900, mb: 0.5 }}
        >
          Pricing will appear here
        </Txt>
        <Txt
          component="div"
          sx={{
            fontSize: 12,
            color: SZ.ink400,
            lineHeight: 1.5,
            maxWidth: 240,
            mx: "auto",
          }}
        >
          Fill out the loan, borrower, property and income details, then run
          Calculate &amp; price.
        </Txt>
      </Card>
    );
  }

  // The guideline screen (2026-10-09). `eligible: false` = a hard row, so the
  // scenario is ineligible even if a rung's own DSCR/LTV gate would pass.
  const guidelineRows = result.guidelineExceptions || [];
  const guidelineEligible = result.eligible !== false && res?.eligible !== false;
  const maxLoan = result.suggestions?.maxLoanAtMaxLeverage ?? result.leverage?.maxLoanAtMaxLeverage ?? null;

  return (
    <ResultStack masonry={masonry}>
      <EligibilityBanner
        passing={guidelineEligible && hasPassing && Boolean(anchorRung?.passing)}
        bandLabel={guidelineEligible ? bandLabel : null}
        anchor={guidelineEligible ? res?.anchor : "guideline"}
      />

      <GuidelineCard exceptions={guidelineRows} maxLoan={maxLoan} />

      {!hideHero && <ExitHero res={res} bounds={bounds} llpas={llpas} />}

      <LeverageCard leverage={result.leverage} maxLoan={maxLoan} />

      <TierBucketsCard res={res} fico={fico} ltv={ltv} />

      <AlternativesCard
        bandBests={result.bandBests}
        bestExecution={result.bestExecution}
        breakeven={result.breakeven}
        anchorCoupon={res?.coupon}
      />

      <LadderCard
        rows={rows}
        anchorCoupon={res?.coupon}
        capLabel={bounds.capLabel}
        onReanchor={onReanchor}
        busy={pending}
        pendingCoupon={pendingCoupon}
      />

      {/* Directly under the ladder: the ladder is where the rate is CHOSEN, so
          the action that holds it belongs against the same block rather than
          below the supporting cards. Hidden for now — see SHOW_RATE_LOCK. */}
      {SHOW_RATE_LOCK && (
        <RateLockCard
          res={res}
          canLock={hasPassing && Boolean(anchorRung?.passing)}
        />
      )}

      <OutcomeMatrixCard matrix={result.outcomeMatrix} />

      <RuralCard ruralCheck={result.ruralCheck} exception={res?.ruralException} />

      {/* The priced response already carries the resolved market block, so this
          renders with no second call. */}
      <MarketDataPanel
        zipcode={inputs?.address?.zip || inputs?.zipCode || zip}
        marketData={result.marketData}
      />

      {result.closingCostLadder?.length > 0 && (
        <Card>
          <CardTitle>Closing cost ladder</CardTitle>
          <ClosingCostLadder rows={result.closingCostLadder} />
        </Card>
      )}

      <CaveatsCard
        res={res}
        bandLabel={bandLabel}
        anchorRung={anchorRung}
        capLabel={bounds.capLabel}
      />
    </ResultStack>
  );
}
